import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import { clientWebPageConfigDict } from '@common/model/meta/clientWebPageConfig';
import { ROUTES_DEFAULT_CITY_ALIAS } from '@common/const/routes/defaultCity';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { ClientWebRouteViewModel } from '@common/model/meta/clientWebRouteViewModel';
import { STATIC_CLIENT_WEB_ROUTES } from './const';
import { RoutesDictionary, RoutesDictionaryEntry } from './types';

const normalizeProps = (props: unknown) => omitBy((props ?? {}) as Record<string, unknown>, isUndefined);

export const createRoutesDict = (routes?: ClientWebRouteViewModel[], staticRoutes = STATIC_CLIENT_WEB_ROUTES): RoutesDictionary => {
  const routesDict: RoutesDictionary = {};

  // 1. Populate initial dictionary from staticRoutes
  staticRoutes.forEach(({ routeId, path, pageId, props, hasCitySeparatedContent: staticHasCity, defaultCityAlias: staticDefaultCity }) => {
    if (!path) {
      return;
    }
    const { hasCitySeparatedContent = false } = clientWebPageConfigDict[pageId] ?? {};

    if (!routesDict[pageId]) {
      routesDict[pageId] = [];
    }

    (routesDict[pageId] as RoutesDictionaryEntry[]).push({
      pageId,
      routeId,
      path,
      props: (props ?? {}) as RoutesDictionaryEntry['props'],
      hasCitySeparatedContent: staticHasCity ?? hasCitySeparatedContent,
      defaultCityAlias: staticDefaultCity ?? ROUTES_DEFAULT_CITY_ALIAS,
    });
  });

  // 2. Merge backend dynamic routes, overriding static defaults with backend permalinks
  routes?.forEach(({ id, path, props, pageId: _pageId }) => {
    if (!path) {
      return;
    }

    const pageId = _pageId as ClientWebPage;
    const { hasCitySeparatedContent = false } = clientWebPageConfigDict[pageId] ?? {};

    if (!routesDict[pageId]) {
      routesDict[pageId] = [];
    }

    const pageEntries = routesDict[pageId] as RoutesDictionaryEntry[];
    const normalizedDynamicProps = normalizeProps(props);

    const existingIndex = pageEntries.findIndex((entry) => isEqual(normalizeProps(entry.props), normalizedDynamicProps));

    const newEntry: RoutesDictionaryEntry = {
      pageId,
      routeId: id,
      path,
      props: (props ?? {}) as RoutesDictionaryEntry['props'],
      hasCitySeparatedContent,
      defaultCityAlias: ROUTES_DEFAULT_CITY_ALIAS,
    };

    if (existingIndex !== -1) {
      pageEntries[existingIndex] = newEntry;
    } else {
      pageEntries.push(newEntry);
    }
  });

  return routesDict;
};
